// ABOUTME: Bottom navigation bar for mobile app navigation
// ABOUTME: 4 primary tabs with child route highlighting based on auth state

import {
  ArrowRightEndOnRectangleIcon,
  BookOpenIcon,
  HomeIcon,
  TrophyIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ShirtIcon } from '@/components/icons'
import type { User as AuthUser } from '@/providers'
import { getAuthService } from '@/services/AuthService'

const publicNavigation = [
  {
    name: 'Home',
    href: '/',
    childRoutes: [] as string[],
    icon: HomeIcon,
    accent: 'text-primary-600',
  },
  {
    name: 'Learn',
    href: '/education',
    childRoutes: ['/challenges'],
    icon: BookOpenIcon,
    accent: 'text-sky-600',
  },
  {
    name: 'Challenges',
    href: '/challenges',
    childRoutes: [],
    icon: TrophyIcon,
    accent: 'text-orange-500',
  },
  {
    name: 'Sign In',
    href: '/auth',
    childRoutes: [],
    icon: ArrowRightEndOnRectangleIcon,
    accent: 'text-primary-600',
  },
]

const authenticatedNavigation = [
  {
    name: 'Home',
    href: '/',
    childRoutes: [] as string[],
    icon: HomeIcon,
    accent: 'text-primary-600',
  },
  {
    name: 'Wardrobe',
    href: '/wardrobe',
    childRoutes: ['/scanner', '/outfit-builder', '/recommendations'],
    icon: ShirtIcon,
    accent: 'text-emerald-600',
  },
  {
    name: 'Learn',
    href: '/education',
    childRoutes: ['/challenges'],
    icon: BookOpenIcon,
    accent: 'text-sky-600',
  },
  {
    name: 'Profile',
    href: '/profile',
    childRoutes: ['/progress', '/insights', '/avatar', '/subscription'],
    icon: UserIcon,
    accent: 'text-purple-600',
  },
]

export default function MobileNavigation() {
  const location = useLocation()
  const [user, setUser] = useState<AuthUser | null | undefined>(undefined)

  useEffect(() => {
    const authService = getAuthService()
    authService.getCurrentUser().then(setUser)

    const unsubscribe = authService.onAuthStateChange(setUser)
    return () => unsubscribe()
  }, [])

  const navigation = user ? authenticatedNavigation : publicNavigation

  const isActive = (item: (typeof navigation)[number]) => {
    if (location.pathname === item.href) return true
    return item.childRoutes.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    )
  }

  if (location.pathname === '/auth') {
    return null
  }

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 safe-bottom">
      <div className="mx-4 mb-4">
        <div className="bg-surface rounded-lg shadow-lg border border-fg-tertiary/20 flex items-center justify-between px-2 py-2">
          {navigation.map((item) => {
            const active = isActive(item)
            const Icon = item.icon

            return (
              <Link
                key={item.name}
                to={item.href}
                className="flex flex-col items-center justify-center py-2 px-3 flex-1"
              >
                <div
                  className={`flex flex-col items-center gap-1 ${
                    active ? item.accent : 'text-fg-tertiary'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="text-xs font-medium">{item.name}</span>
                  {active && <div className="w-1 h-1 rounded-full bg-current" />}
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
